import { Injectable } from '@angular/core';
import { Activity } from '@go-modules/interfaces/activity';
import { Task, TaskType } from '@go-modules/interfaces/task';

const TASK_TYPE_ORDER: Record<TaskType, number> = {
	[TaskType.Present]: 1,
	[TaskType.Reflect]: 2,
	[TaskType.Submit]: 3,
	[TaskType.React]: 4,
	[TaskType.Review]: 5,
	[TaskType.Evaluate]: 6
};

@Injectable({
	providedIn: 'root'
})
export class ActivityPreviewTaskPolicyService {
	public isPreviewTaskType (type: TaskType): boolean {
		return type !== TaskType.Evaluate;
	}

	public getPreviewTasks (tasks: Task[]): Task[] {
		return tasks
			.filter((task) => this.isPreviewTaskType(task.type) && task.deleted_at == null)
			.sort((a, b) => a.sort_order - b.sort_order);
	}

	public resolveInitialTaskType (requested: TaskType | null, tasks: Task[]): TaskType | null {
		const previewTasks = this.getPreviewTasks(tasks);
		if (
			requested != null &&
			previewTasks.some((task) => task.type === requested)
		) {
			return requested;
		}

		return this.getFallbackTaskType(previewTasks);
	}

	public canAccessTaskType (type: TaskType, activity: Activity, tasks: Task[]): boolean {
		return this.isTaskTypeCompatibleWithActivity(type, activity) &&
			this.getPreviewTasks(tasks).some((task) => task.type === type);
	}

	public getDefaultPreviewTaskType (activity: Activity, tasks: Task[]): TaskType | null {
		const previewTasks = this.getPreviewTasks(tasks)
			.filter((task) => this.isTaskTypeCompatibleWithActivity(task.type, activity));

		return this.getFallbackTaskType(previewTasks);
	}

	public getTaskTypeDefaultOrder (type: TaskType): number {
		return TASK_TYPE_ORDER[type] ?? 999;
	}

	public isTaskTypeCompatibleWithActivity (type: TaskType, activity: Activity): boolean {
		switch (type) {
			case TaskType.Reflect:
				return activity.has_response_media;
			case TaskType.Submit:
				return activity.has_response_media &&
					!activity.live_session_enabled &&
					!activity.has_single_recording_attempt;
			case TaskType.React:
				return activity.template?.peer_review === 'write';
			case TaskType.Review:
				return activity.has_response_media || !activity.public_feedback;
			default:
				return true;
		}
	}

	private getFallbackTaskType (tasks: Task[]): TaskType | null {
		const incompleteTask = tasks.find((task) => !task.completed);
		return incompleteTask?.type ?? tasks.at(-1)?.type ?? null;
	}
}
